import type { ComponentProps, FC, ReactNode } from 'react';

import { AppSidebar, SidebarInset, SidebarProvider } from 'design-system';
import { Link, useNavigate } from 'react-router';

import logoSrc from '@/assets/images/gxLogo.svg';

import ExpiryCountdown from './ExpiryCountdown';
import { useAppMenuSchema, useSidebarUserName } from './useAppMenuSchema';

// AppSidebar merges its own onClick (schedules the delayed collapse) onto this component via
// Base UI's `render` prop — spread `...rest` through so that handler actually reaches the
// underlying <a>, instead of being silently dropped by a narrower prop signature.
const RouterLink = ({ to, ...rest }: { to: string } & Omit<ComponentProps<typeof Link>, 'to'>) => (
  <Link
    to={to}
    {...rest}
  />
);

interface NavigationMenuProps {
  children?: ReactNode;
}

/**
 * The application's primary navigation — a collapsible icon rail (`AppSidebar` from
 * `design-system`) alongside the rest of the app shell (`children`), both inside a single
 * `SidebarProvider` since they share the expand/collapse state.
 */
export const NavigationMenu: FC<NavigationMenuProps> = ({ children }) => {
  const navigate = useNavigate();
  const menuSchema = useAppMenuSchema();
  const userName = useSidebarUserName();

  return (
    <SidebarProvider defaultOpen={false}>
      <AppSidebar
        menuSchema={menuSchema}
        logoSrc={logoSrc}
        user={{ name: userName ?? '', company: 'General Index' }}
        onLogout={() => navigate('/logout')}
        linkComponent={RouterLink}
        headerExtra={<ExpiryCountdown />}
      />
      <SidebarInset>{children}</SidebarInset>
    </SidebarProvider>
  );
};
